import type { ReactNode } from "react";
import { Box, Link, Stack } from "@mui/material";
import {
  Alert,
  Divider,
  InputLabel,
  Textarea,
  TextInput,
  Typography,
} from "@trimble/modus-cmx";
import { Trans, useTranslation } from "react-i18next";
import DeviceItem from "@/components/DeviceItem";
import { LINKS } from "@/constants";
import { useAccount } from "@/context/account";
import type { AddDeviceSearchQuery } from "@/gql/graphql";
import { DeviceSearchOutcome } from "@/gql/graphql";
import { getModusEventTarget } from "@/utils/modusEvents";

type AddDeviceSearchResultProps = {
  result: AddDeviceSearchQuery["deviceSearch"];
  note: string;
  onNoteChange: (note: string) => void;
};

type SearchAlert = {
  title: string;
  description?: ReactNode;
};

type ReadOnlyField = {
  id: string;
  label: string;
  value: string;
};

const CURRENT_OWNER_INPUT_ID = "add-device-current-owner";

function AddDeviceSearchResult({
  result,
  note,
  onNoteChange,
}: AddDeviceSearchResultProps) {
  const { t } = useTranslation();
  const { account } = useAccount();

  if (result.outcome === DeviceSearchOutcome.NotFound) {
    return (
      <>
        <Box sx={{ "& .modus-wc-divider": { my: 0 } }}>
          <Divider />
        </Box>
        <Typography
          style={{
            color: "var(--modus-wc-color-gray-6)",
            fontStyle: "italic",
          }}
        >
          {t("devices.add_device_modal.device_not_found")}
        </Typography>
      </>
    );
  }

  const alert = ((): SearchAlert | null => {
    switch (result.outcome) {
      case DeviceSearchOutcome.Found:
        return { title: t("devices.add_device_modal.already_exists_title") };
      case DeviceSearchOutcome.OptInRequired:
        return {
          title: t("devices.add_device_modal.opt_in_required_title"),
          description: t(
            "devices.add_device_modal.opt_in_required_description"
          ),
        };
      case DeviceSearchOutcome.UnableToAdd:
        return {
          title: t("devices.add_device_modal.unable_to_add_title"),
          description: (
            <Trans
              i18nKey="devices.add_device_modal.unable_to_add_description"
              components={[
                <Link
                  key="support-link"
                  href={LINKS.SUPPORT}
                  target="_blank"
                  rel="noopener noreferrer"
                />,
              ]}
            />
          ),
        };
      case DeviceSearchOutcome.TransferEligible:
        return null;
    }
  })();

  const currentOwnerLabel = t("devices.add_device_modal.current_owner");
  const fields = ((): ReadOnlyField[] => {
    switch (result.outcome) {
      case DeviceSearchOutcome.Found:
        return [
          {
            id: "add-device-dealer",
            label: t("devices.add_device_modal.dealer"),
            value: account.companyName,
          },
          {
            id: CURRENT_OWNER_INPUT_ID,
            label: currentOwnerLabel,
            value: result.dealerDevice?.owner?.companyName ?? "",
          },
        ];
      case DeviceSearchOutcome.OptInRequired:
        return [
          {
            id: CURRENT_OWNER_INPUT_ID,
            label: currentOwnerLabel,
            value: t("devices.add_device_modal.withheld_until_opted_in"),
          },
        ];
      case DeviceSearchOutcome.TransferEligible:
        return [
          {
            id: CURRENT_OWNER_INPUT_ID,
            label: currentOwnerLabel,
            value: t("devices.add_device_modal.anonymous_owner"),
          },
        ];
      case DeviceSearchOutcome.UnableToAdd:
        return [];
    }
  })();

  return (
    <Stack spacing={1}>
      <Box sx={{ "& .modus-wc-divider": { my: 0 } }}>
        <Divider />
      </Box>
      {alert && (
        <Alert key={result.outcome} variant="warning" icon="info">
          <div slot="content">
            <Typography weight="bold">{alert.title}</Typography>
            {alert.description !== undefined && (
              <Typography style={{ whiteSpace: "normal" }}>
                {alert.description}
              </Typography>
            )}
          </div>
        </Alert>
      )}
      {result.dealerDevice && (
        <DeviceItem
          device={result.dealerDevice}
          hideCompanyName
          hideExternalBadge
        />
      )}
      <Stack direction="row" gap={2}>
        {fields.map((field) => (
          <Stack key={field.id} width="100%">
            <InputLabel size="lg" forId={field.id} labelText={field.label} />
            <TextInput inputId={field.id} value={field.value} disabled />
          </Stack>
        ))}
      </Stack>
      {result.outcome === DeviceSearchOutcome.TransferEligible && (
        <Stack sx={{ "& .modus-wc-textarea": { mt: 0 } }}>
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
          >
            <InputLabel
              size="lg"
              forId="add-device-note-to-owner"
              labelText={t("devices.add_device_modal.note_to_owner_label")}
            />
            <Typography size="sm">
              {t("devices.add_device_modal.optional")}
            </Typography>
          </Stack>
          <Textarea
            inputId="add-device-note-to-owner"
            value={note}
            rows={3}
            onInputChange={(event) => {
              const target = getModusEventTarget(event, HTMLTextAreaElement);
              if (target) {
                onNoteChange(target.value);
              }
            }}
          />
        </Stack>
      )}
    </Stack>
  );
}

export default AddDeviceSearchResult;
